import type {
  IEmailCampaignDesignStatus,
  TEmailBuilderReferencedMediaMap,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import type { EmailDocument } from "pages/secure/facility/marketing/emailBuilder/domain/types";
import type {
  TEmailBuilderWorkspace,
  TEmailBuilderWorkspaceKind,
} from "pages/secure/facility/marketing/emailBuilder/emailBuilderWorkspace";
import type {
  IEmailBuilderCampaignMeta,
  IEmailBuilderDraftMeta,
  IEmailBuilderTemplateActivation,
  TEmailBuilderActivePanel,
  TEmailBuilderAutoSaveStatus,
  TEmailBuilderLoadStatus,
  TEmailBuilderMode,
  TEmailBuilderPreviewViewport,
} from "redux/reducers/emailBuilder/emailBuilder";
import type { TEmailBuilderAddInsertContext } from "pages/secure/facility/marketing/emailBuilder/emailBuilderAddInsertContextTypes";

export enum EmailBuilderActionType {
  START_LOADING = "emailBuilder/START_LOADING",
  LOAD_SUCCEEDED = "emailBuilder/LOAD_SUCCEEDED",
  LOAD_FAILED = "emailBuilder/LOAD_FAILED",
  EXIT = "emailBuilder/EXIT",
  OPEN_INLINE_CONTENT = "emailBuilder/OPEN_INLINE_CONTENT",
  CLOSE_INLINE_CONTENT = "emailBuilder/CLOSE_INLINE_CONTENT",
  OPEN_BLOCK_SETTINGS = "emailBuilder/OPEN_BLOCK_SETTINGS",
  ENTER_BLOCK_SETTINGS_MODE = "emailBuilder/ENTER_BLOCK_SETTINGS_MODE",
  CLOSE_BLOCK_SETTINGS = "emailBuilder/CLOSE_BLOCK_SETTINGS",
  SYNC_EDITOR_DOCUMENT = "emailBuilder/SYNC_EDITOR_DOCUMENT",
  UPDATE_AUTOSAVE_STATUS = "emailBuilder/UPDATE_AUTOSAVE_STATUS",
  AUTOSAVE_SUCCEEDED = "emailBuilder/AUTOSAVE_SUCCEEDED",
  SYNC_DRAFT_REVISION = "emailBuilder/SYNC_DRAFT_REVISION",
  FLUSH_AUTOSAVE = "emailBuilder/FLUSH_AUTOSAVE",
  CANCEL_PENDING_AUTOSAVE = "emailBuilder/CANCEL_PENDING_AUTOSAVE",
  SET_ACTIVE_PANEL = "emailBuilder/SET_ACTIVE_PANEL",
  CONSUME_ADD_PANEL_INSERT_SUPPRESS = "emailBuilder/CONSUME_ADD_PANEL_INSERT_SUPPRESS",
  SET_MODE = "emailBuilder/SET_MODE",
  SET_PREVIEW_VIEWPORT = "emailBuilder/SET_PREVIEW_VIEWPORT",
  SET_CAMPAIGN_NAME = "emailBuilder/SET_CAMPAIGN_NAME",
  PREVIEW_START = "emailBuilder/PREVIEW_START",
  PREVIEW_SUCCEEDED = "emailBuilder/PREVIEW_SUCCEEDED",
  PREVIEW_FAILED = "emailBuilder/PREVIEW_FAILED",
  TEMPLATE_ACTIVATED = "emailBuilder/TEMPLATE_ACTIVATED",
}

export interface IEmailBuilderRequestIdentity {
  requestId: string;
  campaignId: number;
  workspaceKind?: TEmailBuilderWorkspaceKind;
}

export interface IEmailBuilderStartLoadingAction {
  type: typeof EmailBuilderActionType.START_LOADING;
  payload: IEmailBuilderRequestIdentity;
}

export interface IEmailBuilderLoadSucceededPayload {
  loadStatus: Extract<TEmailBuilderLoadStatus, "succeeded">;
  workspace: TEmailBuilderWorkspace;
  campaign: IEmailBuilderCampaignMeta;
  draft: IEmailBuilderDraftMeta;
  document: EmailDocument;
  referencedMedia: TEmailBuilderReferencedMediaMap;
  campaignDesign?: IEmailCampaignDesignStatus | null;
  templateActivation?: IEmailBuilderTemplateActivation | null;
  requestId?: string;
  campaignId?: number;
}

export interface IEmailBuilderLoadSucceededAction {
  type: typeof EmailBuilderActionType.LOAD_SUCCEEDED;
  payload: IEmailBuilderLoadSucceededPayload;
}

export interface IEmailBuilderLoadFailedPayload {
  loadStatus: Extract<TEmailBuilderLoadStatus, "failed">;
  loadError: string;
  requestId?: string;
  campaignId?: number;
}

export interface IEmailBuilderLoadFailedAction {
  type: typeof EmailBuilderActionType.LOAD_FAILED;
  payload: IEmailBuilderLoadFailedPayload;
}

export interface IEmailBuilderExitAction {
  type: typeof EmailBuilderActionType.EXIT;
}

export interface IEmailBuilderOpenInlineContentAction {
  type: typeof EmailBuilderActionType.OPEN_INLINE_CONTENT;
}

export interface IEmailBuilderCloseInlineContentAction {
  type: typeof EmailBuilderActionType.CLOSE_INLINE_CONTENT;
}

export interface IEmailBuilderOpenBlockSettingsAction {
  type: typeof EmailBuilderActionType.OPEN_BLOCK_SETTINGS;
}

export interface IEmailBuilderEnterBlockSettingsModeAction {
  type: typeof EmailBuilderActionType.ENTER_BLOCK_SETTINGS_MODE;
}

export interface IEmailBuilderCloseBlockSettingsAction {
  type: typeof EmailBuilderActionType.CLOSE_BLOCK_SETTINGS;
}

export interface IEmailBuilderSyncEditorDocumentAction {
  type: typeof EmailBuilderActionType.SYNC_EDITOR_DOCUMENT;
  payload: {
    document: EmailDocument;
  };
}

export interface IEmailBuilderUpdateAutosaveStatusAction {
  type: typeof EmailBuilderActionType.UPDATE_AUTOSAVE_STATUS;
  payload: {
    autoSaveStatus: TEmailBuilderAutoSaveStatus;
    autoSaveError: string | null;
    autoSaveConflict: boolean;
    autoSaveDueAt: number | null;
  };
}

export interface IEmailBuilderAutosaveSucceededAction {
  type: typeof EmailBuilderActionType.AUTOSAVE_SUCCEEDED;
  payload: {
    revision: number;
    updatedAt: string | null;
    baselineDocument: EmailDocument;
  };
}

export interface IEmailBuilderSyncDraftRevisionAction {
  type: typeof EmailBuilderActionType.SYNC_DRAFT_REVISION;
  payload: {
    revision: number;
    updatedAt: string | null;
  };
}

export interface IEmailBuilderFlushAutosaveAction {
  type: typeof EmailBuilderActionType.FLUSH_AUTOSAVE;
  meta?: {
    onComplete?: (ok: boolean) => void;
  };
}

export interface IEmailBuilderCancelPendingAutosaveAction {
  type: typeof EmailBuilderActionType.CANCEL_PENDING_AUTOSAVE;
}

export interface IEmailBuilderSetActivePanelAction {
  type: typeof EmailBuilderActionType.SET_ACTIVE_PANEL;
  payload: {
    activePanel: TEmailBuilderActivePanel;
    addInsertContext?: TEmailBuilderAddInsertContext | null;
  };
}

export interface IEmailBuilderConsumeAddPanelInsertSuppressAction {
  type: typeof EmailBuilderActionType.CONSUME_ADD_PANEL_INSERT_SUPPRESS;
}

export interface IEmailBuilderSetModeAction {
  type: typeof EmailBuilderActionType.SET_MODE;
  payload: {
    mode: TEmailBuilderMode;
  };
}

export interface IEmailBuilderSetPreviewViewportAction {
  type: typeof EmailBuilderActionType.SET_PREVIEW_VIEWPORT;
  payload: {
    previewViewport: TEmailBuilderPreviewViewport;
  };
}

export interface IEmailBuilderSetCampaignNameAction {
  type: typeof EmailBuilderActionType.SET_CAMPAIGN_NAME;
  payload: {
    name: string;
  };
}

export interface IEmailBuilderPreviewStartAction {
  type: typeof EmailBuilderActionType.PREVIEW_START;
  payload: IEmailBuilderRequestIdentity;
}

export interface IEmailBuilderPreviewSucceededAction {
  type: typeof EmailBuilderActionType.PREVIEW_SUCCEEDED;
  payload: {
    previewHtml: string;
    previewRevision: number;
    requestId?: string;
    campaignId?: number;
  };
}

export interface IEmailBuilderPreviewFailedAction {
  type: typeof EmailBuilderActionType.PREVIEW_FAILED;
  payload: {
    previewError: string;
    requestId?: string;
    campaignId?: number;
  };
}

export interface IEmailBuilderTemplateActivatedPayload {
  activeSourceDraftRevision: number;
  activatedAt: string | null;
}

export interface IEmailBuilderTemplateActivatedAction {
  type: typeof EmailBuilderActionType.TEMPLATE_ACTIVATED;
  payload: IEmailBuilderTemplateActivatedPayload;
}

export type TEmailBuilderAction =
  | IEmailBuilderStartLoadingAction
  | IEmailBuilderLoadSucceededAction
  | IEmailBuilderLoadFailedAction
  | IEmailBuilderExitAction
  | IEmailBuilderOpenInlineContentAction
  | IEmailBuilderCloseInlineContentAction
  | IEmailBuilderOpenBlockSettingsAction
  | IEmailBuilderEnterBlockSettingsModeAction
  | IEmailBuilderCloseBlockSettingsAction
  | IEmailBuilderSyncEditorDocumentAction
  | IEmailBuilderUpdateAutosaveStatusAction
  | IEmailBuilderAutosaveSucceededAction
  | IEmailBuilderSyncDraftRevisionAction
  | IEmailBuilderFlushAutosaveAction
  | IEmailBuilderCancelPendingAutosaveAction
  | IEmailBuilderSetActivePanelAction
  | IEmailBuilderConsumeAddPanelInsertSuppressAction
  | IEmailBuilderSetModeAction
  | IEmailBuilderSetPreviewViewportAction
  | IEmailBuilderSetCampaignNameAction
  | IEmailBuilderPreviewStartAction
  | IEmailBuilderPreviewSucceededAction
  | IEmailBuilderPreviewFailedAction
  | IEmailBuilderTemplateActivatedAction;

export type TEmailBuilderAutosaveScheduleAction = IEmailBuilderSyncEditorDocumentAction;

const allowedEmailBuilderActionTypes = new Set<string>(Object.values(EmailBuilderActionType));

const emailBuilderAutosaveScheduleActionTypes = new Set<string>([
  EmailBuilderActionType.SYNC_EDITOR_DOCUMENT,
]);

export function isEmailBuilderAutosaveScheduleAction(action: {
  type: string;
}): action is TEmailBuilderAutosaveScheduleAction {
  return emailBuilderAutosaveScheduleActionTypes.has(action.type);
}

export function isEmailBuilderAction(action: { type: string }): action is TEmailBuilderAction {
  return allowedEmailBuilderActionTypes.has(action.type);
}
